import { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import { joinWaitlist } from '@/lib/api';

/**
 * Minimal editorial waitlist form for a product.
 *
 * Props:
 *   product: 'ceo-intelligence' | 'move-mode'
 *   productLabel: display name (e.g. 'CEO Intelligence')
 *   headline: main heading string
 *   subhead: italic subhead (optional)
 *   body: supporting copy string
 *   variant: 'light' (default, ivory bg) | 'dark' (matte bg)
 *   compact: boolean, tighter form for inline placement
 */
export default function WaitlistForm({
  product,
  productLabel,
  headline,
  subhead,
  body,
  variant = 'light',
  compact = false,
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email) return;
    setLoading(true);
    try {
      await joinWaitlist({ product, name: name.trim(), email: email.trim(), source: `waitlist:${product}` });
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(typeof detail === 'string' ? detail : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === 'dark';
  const textColor = isDark ? 'text-ivory' : 'text-matte';
  const mutedText = isDark ? 'text-ivory/70' : 'text-matte/70';
  const eyebrowColor = isDark ? 'text-gold' : 'text-taupe';
  const italicColor = isDark ? 'text-gold' : 'text-taupe';
  const borderColor = isDark ? 'border-ivory/25' : 'border-taupe/40';
  const inputBg = isDark ? 'bg-transparent' : 'bg-transparent';
  const inputText = isDark ? 'text-ivory placeholder-ivory/40' : 'text-matte placeholder-matte/40';
  const focusBorder = 'focus-within:border-gold';

  if (submitted) {
    return (
      <div
        data-testid={`waitlist-confirmation-${product}`}
        className={`${compact ? '' : 'py-2'}`}
      >
        <div className={`eyebrow ${eyebrowColor} mb-4`}>Confirmed</div>
        <div className={`flex items-start gap-4 ${textColor}`}>
          <div className={`mt-1 w-9 h-9 rounded-full grid place-items-center border ${isDark ? 'border-gold/50' : 'border-gold'} flex-none`}>
            <Check size={16} strokeWidth={1.8} className="text-gold" />
          </div>
          <div>
            <div className={`font-serif-display text-2xl md:text-3xl leading-snug tracking-tight ${textColor}`}>
              You&rsquo;re on the list, <em className={`italic ${italicColor}`}>{name.split(' ')[0] || 'friend'}</em>.
            </div>
            <p className={`mt-3 text-[14px] leading-relaxed ${mutedText} max-w-md`}>
              Thank you for joining the {productLabel} waitlist. You&rsquo;ll be among the first to hear when it opens, quietly and with intention.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`waitlist-form-${product}`}>
      {headline && (
        <div>
          <div className={`eyebrow ${eyebrowColor} mb-3`}>Waitlist</div>
          <h3 className={`font-serif-display text-3xl md:text-4xl leading-[1.05] tracking-tighter ${textColor}`}>
            {headline}
          </h3>
          {subhead && (
            <p className={`mt-3 font-serif-display italic text-xl md:text-2xl ${italicColor} leading-snug`}>
              {subhead}
            </p>
          )}
          {body && <p className={`mt-6 text-[14px] leading-relaxed ${mutedText} max-w-lg`}>{body}</p>}
        </div>
      )}

      <form onSubmit={submit} className={`${headline ? 'mt-8' : ''} grid gap-4 max-w-md`}>
        <label className={`block ${compact ? '' : ''}`}>
          <span className={`sr-only`}>Name</span>
          <div className={`flex items-center border-b ${borderColor} ${focusBorder} transition-colors`}>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              aria-label="Full name"
              data-testid={`waitlist-input-name-${product}`}
              className={`flex-1 ${inputBg} py-3 text-[14px] tracking-wide ${inputText} focus:outline-none`}
              disabled={loading}
            />
          </div>
        </label>

        <label className="block">
          <span className="sr-only">Email</span>
          <div className={`flex items-center border-b ${borderColor} ${focusBorder} transition-colors`}>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              aria-label="Email address"
              data-testid={`waitlist-input-email-${product}`}
              className={`flex-1 ${inputBg} py-3 text-[14px] tracking-wide ${inputText} focus:outline-none`}
              disabled={loading}
            />
          </div>
        </label>

        <div className="mt-2">
          <button
            type="submit"
            disabled={loading}
            data-testid={`waitlist-submit-${product}`}
            className={`inline-flex items-center gap-2 font-label text-[11px] tracking-[0.28em] uppercase pb-1 border-b transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isDark
                ? 'text-ivory border-ivory hover:text-gold hover:border-gold'
                : 'text-matte border-matte hover:text-gold hover:border-gold'
            }`}
          >
            {loading ? 'Adding you…' : 'Join the Waitlist'}
            {!loading && <ArrowRight size={14} strokeWidth={1.6} />}
          </button>
        </div>

        <p className={`mt-2 text-[11px] leading-relaxed ${mutedText}/80 max-w-sm`}>
          We&rsquo;ll only use your email to notify you about {productLabel}. Unsubscribe anytime.
        </p>
      </form>
    </div>
  );
}
